/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  SABRE_CREATE_IMAGE,
  SABRE_CREATE_PRODUCT,
  SABRE_DELETE_IMAGE,
  SABRE_GET_BRANDS,
  SABRE_GET_CATEGORIES,
  SABRE_GET_COMMON_NAMES,
  SABRE_GET_PRODUCT,
  SABRE_UPDATE_PRODUCT,
  SabreCreateImageMutation,
  SabreCreateImageMutationVariables,
  SabreCreateProductMutation,
  SabreCreateProductMutationVariables,
  SabreCategoriesQuery,
  SabreCategoriesQueryVariables,
  SabreDeleteImageMutation,
  SabreDeleteImageMutationVariables,
  SabreGetBrandsQuery,
  SabreGetBrandsQueryVariables,
  SabreCommonNamesQuery,
  SabreCommonNamesQueryVariables,
  SabreGetProductQuery,
  SabreGetProductQueryVariables,
  SabrePackagingType,
  SabrePackagingUnits,
  SabreProductImageType,
  SabreUpdateProductMutation,
  SabreUpdateProductMutationVariables,
} from '@dai/graphql'
import { DZFile, uniqueId } from '@dai/common'
import { uploadBytesResumable, ref } from 'firebase/storage'
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

import { imageStorage } from '../../api/Firebase'
import UserContext from '../../context/UserContext'
import { FirebaseHelpers } from '../../helpers/firebase.helpers'
import ProductDialogFormFields from './ProductDialogFormFields'
import ProductDialogImagePanel from './ProductDialogImagePanel'
import type {
  CarouselImage,
  ProductDialogProps,
  ProductFormValues,
} from './ProductDialog.types'
import {
  getApolloErrorMessage,
  getDefaultImageSearchQuery,
  getInitialValues,
  mapMarketingImages,
  mergeUniqueOptions,
  normalizeSearchTerm,
} from './productDialogUtils'

const {
  VITE_APP_GOOGLE_IMAGE_SEARCH_API_KEY: googleImageSearchApiKey,
  VITE_APP_GOOGLE_IMAGE_SEARCH_ID: googleImageSearchId,
  VITE_APP__FB_PROJECT_ID: firebaseProjectId,
} = import.meta.env

const IMAGE_SEARCH_RESULTS_PER_PAGE = 10
const IMAGE_SEARCH_PAGE_COUNT = 7

function ProductDialog({
  mode,
  open,
  initialProduct,
  onClose,
  onSaved,
}: ProductDialogProps) {
  const { t } = useT()
  const { user } = React.useContext(UserContext)
  const [dialogProduct, setDialogProduct] = React.useState(initialProduct)

  const [values, setValues] = React.useState<ProductFormValues>(() =>
    getInitialValues(mode, initialProduct),
  )
  const [error, setError] = React.useState<string | null>(null)
  const [imageSearchWarning, setImageSearchWarning] = React.useState<
    string | null
  >(null)
  const [transitioning, setTransitioning] = React.useState(false)
  const [brandOptions, setBrandOptions] = React.useState<string[]>([])
  const [categoryOptions, setCategoryOptions] = React.useState<string[]>([])
  const [commonNameOptions, setCommonNameOptions] = React.useState<string[]>([])
  const [pendingCreateImage, setPendingCreateImage] = React.useState<{
    file: DZFile
    previewUri: string
    id: string
  } | null>(null)
  const [selectedPrimaryImage, setSelectedPrimaryImage] = React.useState<{
    uri: string
    id: string
  } | null>(null)
  const [imageSearchQuery, setImageSearchQuery] = React.useState('')
  const [imageSearchLoading, setImageSearchLoading] = React.useState(false)
  const [imageSearchResults, setImageSearchResults] = React.useState<
    Array<{ title: string; imageUrl: string; thumbnailUrl: string }>
  >([])
  const [imageSearchOpen, setImageSearchOpen] = React.useState(false)
  const [imageSearchAnchorEl, setImageSearchAnchorEl] =
    React.useState<HTMLDivElement | null>(null)
  const [imageMutationMessage, setImageMutationMessage] = React.useState<
    string | null
  >(null)
  const [imageDeletingId, setImageDeletingId] = React.useState<string | null>(
    null,
  )
  const [lastResolvedMarketingImages, setLastResolvedMarketingImages] =
    React.useState<CarouselImage[] | null>(null)
  const [editMarketingImages, setEditMarketingImages] = React.useState<
    CarouselImage[] | null
  >(null)

  const uploadInputRef = React.useRef<HTMLInputElement | null>(null)
  const wasOpenRef = React.useRef(false)
  const previousModeRef = React.useRef(mode)

  const [loadBrands] = useLazyQuery<
    SabreGetBrandsQuery,
    SabreGetBrandsQueryVariables
  >(SABRE_GET_BRANDS, {
    context: { endPoint: 'sabre' },
    fetchPolicy: 'network-only',
  })
  const [loadCommonNames] = useLazyQuery<
    SabreCommonNamesQuery,
    SabreCommonNamesQueryVariables
  >(SABRE_GET_COMMON_NAMES, {
    context: { endPoint: 'sabre' },
    fetchPolicy: 'network-only',
  })
  const [loadCategories] = useLazyQuery<
    SabreCategoriesQuery,
    SabreCategoriesQueryVariables
  >(SABRE_GET_CATEGORIES, {
    context: { endPoint: 'sabre' },
    fetchPolicy: 'network-only',
  })

  const [createProduct, { loading: creating }] = useMutation<
    SabreCreateProductMutation,
    SabreCreateProductMutationVariables
  >(SABRE_CREATE_PRODUCT, {
    context: { endPoint: 'sabre' },
  })
  const [updateProduct, { loading: updating }] = useMutation<
    SabreUpdateProductMutation,
    SabreUpdateProductMutationVariables
  >(SABRE_UPDATE_PRODUCT, {
    context: { endPoint: 'sabre' },
  })
  const [createImage] = useMutation<
    SabreCreateImageMutation,
    SabreCreateImageMutationVariables
  >(SABRE_CREATE_IMAGE, {
    context: { endPoint: 'sabre' },
  })
  const [deleteImage] = useMutation<
    SabreDeleteImageMutation,
    SabreDeleteImageMutationVariables
  >(SABRE_DELETE_IMAGE, {
    context: { endPoint: 'sabre' },
  })

  const {
    data: fullProductData,
    previousData: previousFullProductData,
    loading: productImagesLoading,
    refetch,
  } = useQuery<SabreGetProductQuery, SabreGetProductQueryVariables>(
    SABRE_GET_PRODUCT,
    {
      variables: { upc: dialogProduct?.upcA ?? '' },
      skip: !open || mode !== 'edit' || !dialogProduct?.upcA,
      fetchPolicy: 'cache-and-network',
      context: { endPoint: 'sabre' },
    },
  )

  React.useEffect(() => {
    const justOpened = open && !wasOpenRef.current
    const modeChangedWhileOpen = open && previousModeRef.current !== mode

    if (!open || (!justOpened && !modeChangedWhileOpen)) {
      wasOpenRef.current = open
      previousModeRef.current = mode
      return
    }

    const nextDialogProduct = mode === 'edit' ? initialProduct : null
    const nextValues = getInitialValues(mode, initialProduct)

    setDialogProduct(nextDialogProduct)
    setValues(nextValues)
    setError(null)
    setImageSearchWarning(null)
    setPendingCreateImage(current => {
      if (current?.previewUri) {
        URL.revokeObjectURL(current.previewUri)
      }
      return null
    })
    setSelectedPrimaryImage(null)
    setLastResolvedMarketingImages(null)
    setEditMarketingImages(null)
    setImageSearchQuery(
      getDefaultImageSearchQuery(nextDialogProduct, nextValues.name),
    )
    setImageSearchResults([])
    setImageSearchOpen(false)
    wasOpenRef.current = open
    previousModeRef.current = mode
  }, [initialProduct, mode, open])

  React.useEffect(
    () => () => {
      if (pendingCreateImage?.previewUri) {
        URL.revokeObjectURL(pendingCreateImage.previewUri)
      }
    },
    [pendingCreateImage?.previewUri],
  )

  const resolvedProductData =
    fullProductData?.product ?? previousFullProductData?.product ?? null

  React.useEffect(() => {
    if (mode !== 'edit' || !resolvedProductData) return
    const nextImages = mapMarketingImages(
      resolvedProductData.images,
      SabreProductImageType.MARKETING,
    )
    setLastResolvedMarketingImages(current =>
      nextImages.length > 0 ? nextImages : current ?? nextImages,
    )
    setEditMarketingImages(current =>
      current === null || nextImages.length > 0 ? nextImages : current,
    )
  }, [mode, resolvedProductData])

  React.useEffect(() => {
    if (mode !== 'edit' || productImagesLoading || !resolvedProductData) return
    const marketingImages = mapMarketingImages(
      resolvedProductData.images,
      SabreProductImageType.MARKETING,
    )

    setSelectedPrimaryImage(current => {
      if (!marketingImages.length) return null

      const preservedSelection = current
        ? marketingImages.find(image => image.id === current.id)
        : null
      if (preservedSelection) {
        return { id: preservedSelection.id, uri: preservedSelection.uri }
      }

      const resolvedPrimary =
        marketingImages.find(image => image.isPrimary) ?? marketingImages[0]
      return resolvedPrimary
        ? { id: resolvedPrimary.id, uri: resolvedPrimary.uri }
        : null
    })
  }, [mode, productImagesLoading, resolvedProductData])

  React.useEffect(() => {
    if (!open) return

    const query = normalizeSearchTerm(values.brand)
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await loadBrands({
            variables: {
              input: {
                brandName: query || undefined,
                limit: 20,
              },
            },
          })
          setBrandOptions(
            mergeUniqueOptions(
              response.data?.brands.results.map(result => result.name) ?? [],
              values.brand,
            ),
          )
        } catch (brandError) {
          console.error('Failed to load brand options', brandError)
          setBrandOptions(mergeUniqueOptions([], values.brand))
        }
      })()
    }, 200)

    return () => window.clearTimeout(timeoutId)
  }, [loadBrands, open, values.brand])

  React.useEffect(() => {
    if (!open) return

    const query = normalizeSearchTerm(values.commonName)
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await loadCommonNames({
            variables: {
              input: {
                searchTerm: query || undefined,
                limit: 20,
              },
            },
          })
          setCommonNameOptions(
            mergeUniqueOptions(
              response.data?.commonNames.results.map(result => result.name) ??
                [],
              values.commonName,
            ),
          )
        } catch (commonNameError) {
          console.error('Failed to load common name options', commonNameError)
          setCommonNameOptions(mergeUniqueOptions([], values.commonName))
        }
      })()
    }, 200)

    return () => window.clearTimeout(timeoutId)
  }, [loadCommonNames, open, values.commonName])

  React.useEffect(() => {
    if (!open || mode !== 'edit') return

    const query = normalizeSearchTerm(values.category)
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await loadCategories({
            variables: {
              input: {
                categoryName: query || undefined,
                limit: 100,
              },
            },
          })
          setCategoryOptions(
            mergeUniqueOptions(
              response.data?.categories.results.map(result => result.name) ??
                [],
              values.category,
            ),
          )
        } catch (categoryError) {
          console.error('Failed to load category options', categoryError)
          setCategoryOptions(mergeUniqueOptions([], values.category))
        }
      })()
    }, 200)

    return () => window.clearTimeout(timeoutId)
  }, [loadCategories, mode, open, values.category])

  const saving = creating || updating || transitioning
  const currentProductUpc = mode === 'edit' ? dialogProduct?.upcA ?? null : null
  const marketingImages = React.useMemo(
    () =>
      mode === 'edit'
        ? editMarketingImages ??
          (resolvedProductData
            ? mapMarketingImages(
                resolvedProductData.images,
                SabreProductImageType.MARKETING,
              )
            : lastResolvedMarketingImages ?? [])
        : pendingCreateImage
          ? [
              {
                uri: pendingCreateImage.previewUri,
                id: pendingCreateImage.id,
                isPrimary: true,
              },
            ]
          : [],
    [
      lastResolvedMarketingImages,
      editMarketingImages,
      mode,
      pendingCreateImage,
      resolvedProductData,
    ],
  )
  const effectivePrimaryImageId =
    selectedPrimaryImage?.id ??
    marketingImages.find(image => image.isPrimary)?.id ??
    marketingImages[0]?.id ??
    null
  const primaryImageUri =
    marketingImages.find(image => image.id === effectivePrimaryImageId)?.uri ??
    selectedPrimaryImage?.uri ??
    marketingImages.find(image => image.isPrimary)?.uri ??
    marketingImages[0]?.uri
  const imageMutating = Boolean(imageMutationMessage)

  const setField = React.useCallback(
    (field: keyof ProductFormValues, nextValue: string) => {
      setValues(current => ({ ...current, [field]: nextValue }))
    },
    [],
  )

  const uploadMarketingImage = React.useCallback(
    async (file: DZFile, upc: string) => {
      if (!user?.email) {
        throw new Error(
          t(
            'correction.productDialog.imageUploadNotAuthenticated',
            'You must be signed in to upload marketing images.',
          ),
        )
      }

      const tenant = await FirebaseHelpers.getUserTenant(user.email)
      if (!tenant) {
        throw new Error(
          t(
            'correction.productDialog.imageUploadTenantMissing',
            'Could not determine your tenant for image upload.',
          ),
        )
      }

      const imageId = uniqueId()
      const filePath = file.path || file.name
      const extension = filePath.slice(filePath.lastIndexOf('.') + 1)
      const uploadFilePath = `${tenant}/productMarketing/${imageId}.${extension}`
      const storageRef = ref(imageStorage, uploadFilePath)
      const uploadTask = uploadBytesResumable(storageRef, file)

      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed', undefined, reject, () => resolve())
      })

      const createImageResponse = await createImage({
        variables: {
          input: {
            bucketFileName: `${firebaseProjectId}-images/${uploadFilePath}`,
            upc,
            imageType: SabreProductImageType.MARKETING,
          },
        },
      })

      const image = createImageResponse.data?.createImage?.image
      if (!image?.url) {
        throw new Error(
          t(
            'correction.productDialog.imageUploadFailed',
            'Failed to save the uploaded marketing image.',
          ),
        )
      }

      return { id: image.id, uri: image.url }
    },
    [createImage, t, user?.email],
  )

  const handleRunImageSearch = React.useCallback(async () => {
    if (!googleImageSearchApiKey || !googleImageSearchId) {
      setError(
        t(
          'correction.productDialog.imageSearchUnavailable',
          'Image search is not configured in this environment. Missing VITE_APP_GOOGLE_IMAGE_SEARCH_API_KEY or VITE_APP_GOOGLE_IMAGE_SEARCH_ID.',
        ),
      )
      return
    }

    const query = imageSearchQuery.trim() || values.name.trim()
    if (!query) {
      setError(
        t(
          'correction.productDialog.imageSearchQueryRequired',
          'Enter a product name before searching for marketing images.',
        ),
      )
      return
    }

    setError(null)
    setImageSearchWarning(null)
    setImageSearchLoading(true)
    try {
      const pageResults = await Promise.allSettled(
        Array.from(
          { length: IMAGE_SEARCH_PAGE_COUNT },
          async (_, pageIndex) => {
            const params = new URLSearchParams({
              key: googleImageSearchApiKey,
              cx: googleImageSearchId,
              searchType: 'image',
              num: String(IMAGE_SEARCH_RESULTS_PER_PAGE),
              start: String(pageIndex * IMAGE_SEARCH_RESULTS_PER_PAGE + 1),
              q: query,
            })
            const response = await fetch(
              `https://www.googleapis.com/customsearch/v1?${params.toString()}`,
            )

            if (!response.ok) {
              throw new Error(
                `Image search failed with status ${response.status}`,
              )
            }

            return (await response.json()) as {
              items?: Array<{
                title?: string
                link?: string
                image?: { thumbnailLink?: string }
              }>
            }
          },
        ),
      )
      const imageSearchPages = pageResults.flatMap(result =>
        result.status === 'fulfilled' ? [result.value] : [],
      )
      const failedPages = pageResults.filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected',
      )
      failedPages.forEach(result => {
        console.error('Failed to fetch image search page', result.reason)
      })
      if (imageSearchPages.length && failedPages.length) {
        setImageSearchWarning(
          t(
            'correction.productDialog.imageSearchPartialFailure',
            'Some image search results could not be loaded. Showing the results that were found.',
          ),
        )
      }
      if (!imageSearchPages.length) {
        throw new Error(
          t(
            'correction.productDialog.imageSearchFailed',
            'Failed to search for marketing images. Please try again.',
          ),
        )
      }

      const resultsByUrl = new Map<
        string,
        { title: string; imageUrl: string; thumbnailUrl: string }
      >()
      imageSearchPages
        .flatMap(page => page.items || [])
        .forEach(item => {
          if (!item.link || resultsByUrl.has(item.link)) return
          resultsByUrl.set(item.link, {
            title: item.title || 'Image result',
            imageUrl: item.link,
            thumbnailUrl: item.image?.thumbnailLink || item.link,
          })
        })

      const results = Array.from(resultsByUrl.values())

      setImageSearchResults(results)
      setImageSearchOpen(Boolean(results.length))
    } catch (searchError) {
      setError(
        getApolloErrorMessage(
          searchError,
          t(
            'correction.productDialog.imageSearchFailed',
            'Failed to search for marketing images. Please try again.',
          ),
        ),
      )
      setImageSearchResults([])
      setImageSearchOpen(false)
    } finally {
      setImageSearchLoading(false)
    }
  }, [imageSearchQuery, t, values.name])

  const handleUploadOrStageImage = React.useCallback(
    async (file: DZFile) => {
      if (mode === 'create') {
        setPendingCreateImage(current => {
          if (current?.previewUri) {
            URL.revokeObjectURL(current.previewUri)
          }
          return current
        })
        const previewUri = URL.createObjectURL(file)
        const previewId = uniqueId()
        setPendingCreateImage({ file, previewUri, id: previewId })
        setSelectedPrimaryImage({ id: previewId, uri: previewUri })
        return
      }

      if (!currentProductUpc) return

      try {
        setImageMutationMessage(
          t(
            'correction.productDialog.imageUploadInProgress',
            'Uploading marketing image…',
          ),
        )
        const uploadedImage = await uploadMarketingImage(
          file,
          currentProductUpc,
        )
        setEditMarketingImages(current => [
          ...(current ?? []),
          { id: uploadedImage.id, uri: uploadedImage.uri, isPrimary: false },
        ])
        await refetch()
      } catch (uploadError) {
        setError(
          getApolloErrorMessage(
            uploadError,
            t(
              'correction.productDialog.imageUploadFailed',
              'Failed to save the uploaded marketing image.',
            ),
          ),
        )
      } finally {
        setImageMutationMessage(null)
      }
    },
    [currentProductUpc, mode, refetch, t, uploadMarketingImage],
  )

  const handleUseImageSearchResult = React.useCallback(
    async (result: {
      title: string
      imageUrl: string
      thumbnailUrl: string
    }) => {
      try {
        setError(null)
        const response = await fetch(result.imageUrl)
        if (!response.ok) {
          throw new Error(
            `Image download failed with status ${response.status}`,
          )
        }

        const blob = await response.blob()
        const extension =
          blob.type.split('/')[1]?.split('+')[0]?.toLowerCase() || 'jpg'
        const fileName = `${uniqueId()}.${extension}`
        const file = new File([blob], fileName, {
          type: blob.type || 'image/jpeg',
        }) as DZFile
        file.path = fileName

        await handleUploadOrStageImage(file)
        setImageSearchOpen(false)
      } catch (downloadError) {
        setError(
          getApolloErrorMessage(
            downloadError,
            t(
              'correction.productDialog.imageUseFailed',
              'Failed to use the selected marketing image.',
            ),
          ),
        )
      }
    },
    [handleUploadOrStageImage, t],
  )

  const handleDeleteMarketingImage = React.useCallback(
    async (imageId: string) => {
      if (mode === 'create') {
        setPendingCreateImage(current => {
          if (current?.previewUri) {
            URL.revokeObjectURL(current.previewUri)
          }
          return null
        })
        setSelectedPrimaryImage(null)
        return
      }

      try {
        setImageDeletingId(imageId)
        setImageMutationMessage(
          t(
            'correction.productDialog.imageDeleteInProgress',
            'Deleting marketing image…',
          ),
        )
        await deleteImage({ variables: { input: { id: imageId } } })
        setEditMarketingImages(current =>
          (current ?? []).filter(image => image.id !== imageId),
        )
        if (selectedPrimaryImage?.id === imageId) {
          setSelectedPrimaryImage(null)
        }
        await refetch()
      } catch (deleteError) {
        setError(
          getApolloErrorMessage(
            deleteError,
            t(
              'correction.productDialog.imageDeleteFailed',
              'Failed to delete the marketing image.',
            ),
          ),
        )
      } finally {
        setImageDeletingId(null)
        setImageMutationMessage(null)
      }
    },
    [deleteImage, mode, refetch, selectedPrimaryImage?.id, t],
  )

  const handleUploadInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return
      const dzFile = file as DZFile
      dzFile.path = file.name
      void handleUploadOrStageImage(dzFile)
      event.target.value = ''
    },
    [handleUploadOrStageImage],
  )

  const handleSubmit = React.useCallback(async () => {
    setError(null)

    const trimmedUpc = values.upc.trim()
    const trimmedName = values.name.trim()
    const trimmedCommonName = values.commonName.trim()
    const trimmedCategory = values.category.trim()
    const trimmedBrand = values.brand.trim()

    if (!trimmedName) {
      setError(t('correction.productDialog.nameRequired', 'Name is required.'))
      return
    }
    if (!trimmedCommonName) {
      setError(
        t(
          'correction.productDialog.commonNameRequired',
          'Common name is required.',
        ),
      )
      return
    }
    if (!trimmedBrand) {
      setError(
        t('correction.productDialog.brandRequired', 'Brand is required.'),
      )
      return
    }

    const hasAnyPackagingField = [
      values.packagingQuantity,
      values.packagingSize,
      values.packagingUnit,
      values.packagingContainer,
    ].some(Boolean)

    let packaging:
      | NonNullable<SabreCreateProductMutationVariables['input']['packaging']>
      | undefined

    if (hasAnyPackagingField) {
      const quantity = Number(values.packagingQuantity)
      const size = Number(values.packagingSize)

      if (!Number.isInteger(quantity) || quantity <= 0) {
        setError(
          t(
            'correction.productDialog.packagingQuantityError',
            'Packaging quantity must be a whole number greater than zero.',
          ),
        )
        return
      }
      if (!Number.isFinite(size) || size <= 0) {
        setError(
          t(
            'correction.productDialog.packagingSizeError',
            'Packaging size must be a number greater than zero.',
          ),
        )
        return
      }

      packaging = {
        quantity,
        size,
        unit: values.packagingUnit
          ? (values.packagingUnit as SabrePackagingUnits)
          : undefined,
        container: values.packagingContainer
          ? (values.packagingContainer as SabrePackagingType)
          : undefined,
      }
    }

    try {
      if (mode === 'create') {
        if (!trimmedUpc) {
          setError(
            t(
              'correction.productDialog.upcRequired',
              'UPC is required to create a product.',
            ),
          )
          return
        }
        if (!/^\d{12}$/.test(trimmedUpc)) {
          setError(
            t(
              'correction.productDialog.upcFormatError',
              'UPC must be exactly 12 digits.',
            ),
          )
          return
        }

        const response = await createProduct({
          variables: {
            input: {
              upc: trimmedUpc,
              name: trimmedName || undefined,
              commonName: trimmedCommonName || undefined,
              category: trimmedCategory || undefined,
              brand: trimmedBrand || undefined,
              packaging,
            },
          },
        })

        const savedProduct = response.data?.createProduct?.product
        if (!savedProduct) {
          throw new Error(
            t(
              'correction.productDialog.createFailure',
              'Failed to create product.',
            ),
          )
        }

        setTransitioning(true)
        try {
          if (pendingCreateImage) {
            const uploadedImage = await uploadMarketingImage(
              pendingCreateImage.file,
              savedProduct.upcA,
            )
            await updateProduct({
              variables: {
                input: {
                  upc: savedProduct.upcA,
                  primaryMarketingImage: {
                    id: uploadedImage.id,
                    url: uploadedImage.uri,
                  },
                },
              },
            })
            URL.revokeObjectURL(pendingCreateImage.previewUri)
            setPendingCreateImage(null)
          }
          await onSaved(savedProduct.upcA)
          onClose()
        } finally {
          setTransitioning(false)
        }
        return
      }

      const existingUpc = initialProduct?.upcA ?? trimmedUpc
      if (!existingUpc) {
        setError(
          t(
            'correction.productDialog.editMissingUpc',
            'No product is available to edit.',
          ),
        )
        return
      }

      const response = await updateProduct({
        variables: {
          input: {
            upc: existingUpc,
            name: trimmedName || undefined,
            commonName: trimmedCommonName || undefined,
            category: trimmedCategory || undefined,
            brand: trimmedBrand || undefined,
            packaging,
            primaryMarketingImage: selectedPrimaryImage
              ? {
                  id: selectedPrimaryImage.id,
                  url: selectedPrimaryImage.uri,
                }
              : undefined,
          },
        },
      })

      const savedProduct = response.data?.updateProduct?.product
      if (!savedProduct) {
        throw new Error(
          t(
            'correction.productDialog.updateFailure',
            'Failed to update product.',
          ),
        )
      }

      setTransitioning(true)
      try {
        await onSaved(savedProduct.upcA)
        onClose()
      } finally {
        setTransitioning(false)
      }
    } catch (submitError) {
      setTransitioning(false)
      setError(
        getApolloErrorMessage(
          submitError,
          mode === 'create'
            ? t(
                'correction.productDialog.createFailure',
                'Failed to create product.',
              )
            : t(
                'correction.productDialog.updateFailure',
                'Failed to update product.',
              ),
        ),
      )
    }
  }, [
    createProduct,
    initialProduct?.upcA,
    mode,
    onClose,
    onSaved,
    pendingCreateImage,
    selectedPrimaryImage,
    t,
    updateProduct,
    uploadMarketingImage,
    values,
  ])

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        {mode === 'create'
          ? t('correction.productDialog.createTitle', 'Create product')
          : t('correction.productDialog.editTitle', 'Edit product')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {imageSearchWarning ? (
            <Alert
              severity="warning"
              onClose={() => setImageSearchWarning(null)}
            >
              {imageSearchWarning}
            </Alert>
          ) : null}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems="flex-start"
          >
            <ProductDialogImagePanel
              mode={mode}
              saving={saving}
              productImagesLoading={productImagesLoading}
              imageSearchLoading={imageSearchLoading}
              imageMutating={imageMutating}
              imageMutationMessage={imageMutationMessage}
              imageDeletingId={imageDeletingId}
              primaryImageUri={primaryImageUri}
              effectivePrimaryImageId={effectivePrimaryImageId}
              marketingImages={marketingImages}
              imageSearchQuery={imageSearchQuery}
              imageSearchResults={imageSearchResults}
              imageSearchOpen={imageSearchOpen}
              uploadInputRef={node => {
                uploadInputRef.current = node
              }}
              imageSearchAnchorEl={imageSearchAnchorEl}
              imageSearchAnchorRef={node => {
                setImageSearchAnchorEl(node)
              }}
              onImageSearchQueryChange={value => {
                setImageSearchQuery(value)
                if (!value.trim()) {
                  setImageSearchResults([])
                  setImageSearchOpen(false)
                }
              }}
              onSearch={() => void handleRunImageSearch()}
              onCloseSearch={() => {
                setImageSearchOpen(false)
                setError(null)
                setImageSearchWarning(null)
              }}
              onUploadClick={() => uploadInputRef.current?.click()}
              onUploadInputChange={handleUploadInputChange}
              onUseSearchResult={result =>
                void handleUseImageSearchResult(result)
              }
              onSelectPrimaryImage={image =>
                setSelectedPrimaryImage({ id: image.id, uri: image.uri })
              }
              onDeleteImage={imageId =>
                void handleDeleteMarketingImage(imageId)
              }
            />
            <ProductDialogFormFields
              mode={mode}
              saving={saving}
              values={values}
              brandOptions={brandOptions}
              commonNameOptions={commonNameOptions}
              categoryOptions={categoryOptions}
              onSetField={setField}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={saving}
        >
          {mode === 'create'
            ? t('correction.productDialog.createAction', 'Create product')
            : t('correction.productDialog.editAction', 'Save product')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductDialog
